import { useEffect, useRef, useState } from 'react'
import { Position, type NodeProps, type NodeTypes } from '@xyflow/react'
import type { AppNode, DecisionBranchId } from '../../types'
import {
  NODE_DEFAULTS,
  resolveDecisionBranchLabel,
  useFlowStore,
} from '../../store/flowStore'
import { darkenForBorder } from '../../utils/nodeColor'
import { NodeShell, type HandleSpec } from './NodeShell'

function StartEndNode({ id, selected, data }: NodeProps<AppNode>) {
  return (
    <NodeShell
      id={id}
      selected={selected}
      data={data}
      kind="startEnd"
      minWidth={120}
      minHeight={50}
    />
  )
}

function ProcessNode({ id, selected, data }: NodeProps<AppNode>) {
  return (
    <NodeShell
      id={id}
      selected={selected}
      data={data}
      kind="process"
      minWidth={120}
      minHeight={50}
    />
  )
}

function InputOutputNode({ id, selected, data }: NodeProps<AppNode>) {
  return (
    <NodeShell
      id={id}
      selected={selected}
      data={data}
      kind="inputOutput"
      minWidth={140}
      minHeight={50}
    />
  )
}

const DECISION_HANDLES: HandleSpec[] = [
  { id: 'top', position: Position.Top },
  { id: 'yes', position: Position.Right },
  { id: 'no', position: Position.Bottom },
  { id: 'left', position: Position.Left },
]

const BRANCH_HINT_CLASS: Record<DecisionBranchId, string> = {
  top: 'decision-hint-top',
  yes: 'decision-hint-yes',
  no: 'decision-hint-no',
  left: 'decision-hint-left',
}

function DecisionBranchHint({
  nodeId,
  branchId,
  selected,
}: {
  nodeId: string
  branchId: DecisionBranchId
  selected: boolean
}) {
  const data = useFlowStore(
    (s) => s.nodes.find((n) => n.id === nodeId)?.data,
  )
  const setDecisionBranchLabel = useFlowStore((s) => s.setDecisionBranchLabel)
  const presentationMode = useFlowStore((s) => s.presentationMode)
  const readOnly = useFlowStore((s) => s.readOnly)
  const locked = presentationMode || readOnly

  const label = resolveDecisionBranchLabel(data, branchId)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(label)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!editing) setDraft(label)
  }, [label, editing])

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing])

  const show = selected || Boolean(label) || editing
  if (!show) return null

  const commit = () => {
    setEditing(false)
    const next = draft.trim()
    if (next !== label) setDecisionBranchLabel(nodeId, branchId, next)
  }

  return (
    <span
      className={`decision-hint ${BRANCH_HINT_CLASS[branchId]} ${editing ? 'editing' : ''} ${selected ? 'selected' : ''}`}
      onDoubleClick={(e) => {
        e.stopPropagation()
        if (locked) return
        setDraft(label)
        setEditing(true)
      }}
      title="Duplo clique para editar o rótulo desta saída"
    >
      {editing ? (
        <input
          ref={inputRef}
          className="decision-hint-input nodrag nopan"
          value={draft}
          placeholder="rótulo…"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Enter') commit()
            if (e.key === 'Escape') {
              setDraft(label)
              setEditing(false)
            }
          }}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="decision-hint-text">{label || '…'}</span>
      )}
    </span>
  )
}

function DecisionNode({ id, selected, data }: NodeProps<AppNode>) {
  // fill/stroke inline: html-to-image não resolve var(--node-color) em SVG (vira preto)
  const fill = data.color ?? NODE_DEFAULTS.decision.color
  const stroke = darkenForBorder(fill)

  return (
    <NodeShell
      id={id}
      selected={selected}
      data={data}
      kind="decision"
      handles={DECISION_HANDLES}
      minWidth={140}
      minHeight={90}
      shape={
        <>
          <svg
            className="diamond-svg"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <polygon
              points="50,2 98,50 50,98 2,50"
              fill={fill}
              stroke={stroke}
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {(Object.keys(BRANCH_HINT_CLASS) as DecisionBranchId[]).map(
            (branchId) => (
              <DecisionBranchHint
                key={branchId}
                nodeId={id}
                branchId={branchId}
                selected={Boolean(selected)}
              />
            ),
          )}
        </>
      }
    />
  )
}

function AnnotationNode({ id, selected, data }: NodeProps<AppNode>) {
  return (
    <NodeShell
      id={id}
      selected={selected}
      data={data}
      kind="annotation"
      handles={[]}
      minWidth={140}
      minHeight={60}
    />
  )
}

function GroupNode({ id, selected, data }: NodeProps<AppNode>) {
  return (
    <NodeShell
      id={id}
      selected={selected}
      data={data}
      kind="group"
      minWidth={240}
      minHeight={160}
    />
  )
}

export const nodeTypes: NodeTypes = {
  startEnd: StartEndNode,
  process: ProcessNode,
  decision: DecisionNode,
  inputOutput: InputOutputNode,
  annotation: AnnotationNode,
  group: GroupNode,
}
